/**
 * THƯ MỤC TOOL + «ĐÃ UPLOAD» (07/10/2026, chủ dự án: «Những folder đã upload lên kênh bạn có thể bốc sang Folder
 * đã Upload»): video tải xong thì app chuyển thư mục tool của nó (trên máy người dùng) vào thư mục «Đã Upload»
 * cùng cấp, để nhìn thư mục tool là biết còn gì chưa đăng.
 *
 * Trình duyệt chỉ ghi được vào đĩa qua File System Access API (Chrome / Edge / Cốc Cốc trên máy tính): người
 * dùng chọn thư mục tool MỘT LẦN và cho quyền ghi; quyền và «tay cầm» thư mục được giữ trong IndexedDB để lần sau
 * chỉ cần bấm «Cho phép». API không dời được THƯ MỤC, nên «chuyển» = tạo thư mục đích trong «Đã Upload», đưa từng
 * file sang, rồi xoá thư mục cũ. Từng file: Chrome / Edge mới có `FileSystemFileHandle.move()` cho cả file ngoài
 * trình duyệt (Intent to Ship M111, 2023) — dời tức thì trên cùng ổ, không chép. Bản đầu (07/10/2026) CHÉP file
 * video vài GB, nhiều video xong gần nhau thì chép đồng thời trên một ổ cứng → chủ dự án 16/10/2026: «bốc video
 * đã upload đang bị đơ hoặc copy lâu». Giờ: thử dời, không được mới chép — từng khúc 32 MB để báo tiến độ và
 * trang không đơ; các lần chuyển nối đuôi nhau (xem `moveAfterUpload` ở store).
 */
import { create } from 'zustand';
import type { DroppedFile, UploadKind } from '@/domain/video-upload';

export const UPLOADED_FOLDER = 'Đã Upload';
const DB_NAME = 'halo-tool-folder';
const STORE = 'handles';
// Hai thư mục tool nhớ riêng (27/10/2026): video dài (khoá `root` như cũ) và video ngắn — tool MV xuất ra ổ khác.
const keyOf = (kind: UploadKind): string => (kind === 'short' ? 'root-short' : 'root');
const pickerIdOf = (kind: UploadKind): string =>
  kind === 'short' ? 'halo-tool-root-short' : 'halo-tool-root';

/** Phần của File System Access API app dùng — khai riêng để không phụ thuộc gói kiểu ngoài. */
export interface ToolFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<{
    write(chunk: Blob | BufferSource | string): Promise<void>;
    close(): Promise<void>;
  }>;
  /** Dời / đổi tên tức thì (Chrome / Edge mới; thư mục thì chưa có). Không có hay ném lỗi thì app chép. */
  move?(destination: ToolDirectoryHandle, name?: string): Promise<void>;
}
export interface ToolDirectoryHandle {
  readonly kind: 'directory';
  readonly name: string;
  values(): AsyncIterable<ToolFileHandle | ToolDirectoryHandle>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<ToolDirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<ToolFileHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  queryPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

type Picker = (options: { mode: 'readwrite'; id: string }) => Promise<ToolDirectoryHandle>;

export interface ToolFolderState {
  /** Đã cho quyền ghi — chuyển được. */
  root: ToolDirectoryHandle | null;
  /** Tay cầm giữ từ lần trước, chờ người dùng bấm «Cho phép» (trình duyệt đòi một cú bấm). */
  pending: ToolDirectoryHandle | null;
  name: string;
  busy: boolean;
}

const emptyToolFolder = (): ToolFolderState => ({
  root: null,
  pending: null,
  name: '',
  busy: false,
});
/** Thư mục tool của VIDEO DÀI (tên cũ giữ nguyên cho mã và kiểm thử sẵn có). */
export const useToolFolderStore = create<ToolFolderState>(emptyToolFolder);
/** Thư mục tool của VIDEO NGẮN (tool MV). */
export const useShortToolFolderStore = create<ToolFolderState>(emptyToolFolder);
export const toolFolderStoreOf = (kind: UploadKind) =>
  kind === 'short' ? useShortToolFolderStore : useToolFolderStore;

/** Trình duyệt có cho chọn thư mục để ghi không (Chrome / Edge / Cốc Cốc máy tính; Firefox, Safari: không). */
export function supportsToolFolder(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB'));
  });
}

async function readStoredRoot(kind: UploadKind): Promise<ToolDirectoryHandle | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(keyOf(kind));
      request.onsuccess = () =>
        resolve((request.result as ToolDirectoryHandle | undefined) ?? null);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB'));
    });
  } catch {
    return null;
  }
}

async function storeRoot(handle: ToolDirectoryHandle | null, kind: UploadKind): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
      const request = handle ? store.put(handle, keyOf(kind)) : store.delete(keyOf(kind));
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error ?? new Error('IndexedDB'));
    });
  } catch {
    /* không giữ được thì lần sau chọn lại — không chặn việc chính */
  }
}

/** Lúc mở màn Tải lên: lấy lại thư mục đã chọn lần trước; còn quyền thì dùng ngay, không thì chờ «Cho phép». */
export async function restoreToolRoot(kind: UploadKind = 'long'): Promise<void> {
  if (!supportsToolFolder()) return;
  const handle = await readStoredRoot(kind);
  if (!handle) return;
  const state = (await handle.queryPermission?.({ mode: 'readwrite' })) ?? 'prompt';
  if (state === 'granted') await adoptRoot(handle, kind);
  else toolFolderStoreOf(kind).setState({ root: null, pending: handle, name: handle.name });
}

/**
 * Dùng thư mục làm thư mục tool và TẠO NGAY «Đã Upload» trong đó (09/10/2026, chủ dự án: «Tự tạo folder Đã Upload
 * và tự cút folder sang, để tránh nhầm lẫn») — nhìn vào là thấy chỗ video đã lên sẽ về.
 */
async function adoptRoot(handle: ToolDirectoryHandle, kind: UploadKind): Promise<void> {
  toolFolderStoreOf(kind).setState({ root: handle, pending: null, name: handle.name });
  try {
    await handle.getDirectoryHandle(UPLOADED_FOLDER, { create: true });
  } catch {
    // Không tạo được lúc này thì lúc chuyển thư mục đầu tiên tạo — không chặn việc chính.
  }
}

/** Đường dẫn nằm trong «Đã Upload» — video đã lên, không đưa vào hàng đợi lần nữa. */
export const isInUploaded = (path: string): boolean =>
  path.split('/').some((segment) => segment.trim() === UPLOADED_FOLDER);

/**
 * Thư mục kéo / chọn vào là THƯ MỤC TỔNG (chứa các thư mục video, như «UPLOAD») chứ không phải một thư mục video:
 * có file nằm sâu ít nhất hai tầng («UPLOAD/KÊNH 0007/KÊNH 0007.mp4»).
 */
export function isParentFolder(files: readonly DroppedFile[]): boolean {
  return files.some((item) => item.path.split('/').filter(Boolean).length >= 3);
}

/**
 * Thư mục tổng vừa KÉO vào (tay cầm lấy lúc thả): xin quyền ghi để tự chuyển; trình duyệt chưa cho hỏi ngay (cần cú
 * bấm) thì giữ chờ nút «Cho phép». Trả về đã dùng được ngay chưa.
 */
export async function adoptDroppedRoot(
  handle: ToolDirectoryHandle,
  kind: UploadKind = 'long',
): Promise<boolean> {
  if (handle.name === UPLOADED_FOLDER) return false;
  let state = (await handle.queryPermission?.({ mode: 'readwrite' })) ?? 'prompt';
  if (state !== 'granted') {
    try {
      state = (await handle.requestPermission?.({ mode: 'readwrite' })) ?? 'denied';
    } catch {
      state = 'prompt';
    }
  }
  await storeRoot(handle, kind);
  if (state === 'granted') {
    await adoptRoot(handle, kind);
    return true;
  }
  toolFolderStoreOf(kind).setState({ root: null, pending: handle, name: handle.name });
  return false;
}

/** Mọi file trong thư mục (đường dẫn tương đối bắt đầu bằng tên thư mục), bỏ qua «Đã Upload». */
export async function readDirectoryFiles(
  directory: ToolDirectoryHandle,
  base = directory.name,
): Promise<DroppedFile[]> {
  const out: DroppedFile[] = [];
  for await (const entry of directory.values()) {
    const path = `${base}/${entry.name}`;
    if (entry.kind === 'directory') {
      if (entry.name === UPLOADED_FOLDER) continue;
      out.push(...(await readDirectoryFiles(entry, path)));
    } else {
      out.push({ file: await entry.getFile(), path });
    }
  }
  return out;
}

/**
 * Nút «Chọn thư mục» của ô tải lên (09/10/2026): mở hộp chọn thư mục CÓ QUYỀN GHI, đọc file trong đó, và nếu là
 * thư mục tổng thì dùng luôn làm thư mục tool — không cần bấm nút riêng nữa. `null` = người dùng bấm Huỷ.
 */
export async function pickFolderWithTool(kind: UploadKind = 'long'): Promise<DroppedFile[] | null> {
  const picker = (window as unknown as { showDirectoryPicker: Picker }).showDirectoryPicker;
  let handle: ToolDirectoryHandle;
  try {
    handle = await picker({ mode: 'readwrite', id: pickerIdOf(kind) });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return null;
    throw error;
  }
  if (handle.name === UPLOADED_FOLDER) {
    throw new Error(
      `Bạn vừa chọn chính thư mục «${UPLOADED_FOLDER}» — hãy chọn thư mục CHỨA các thư mục video`,
    );
  }
  const files = await readDirectoryFiles(handle);
  if (isParentFolder(files)) {
    await adoptRoot(handle, kind);
    await storeRoot(handle, kind);
  }
  return files;
}

/** «Cho phép» với thư mục giữ từ lần trước (cần cú bấm của người dùng). */
export async function allowPendingRoot(kind: UploadKind = 'long'): Promise<boolean> {
  const { pending } = toolFolderStoreOf(kind).getState();
  if (!pending) return false;
  const state = (await pending.requestPermission?.({ mode: 'readwrite' })) ?? 'denied';
  if (state !== 'granted') return false;
  await adoptRoot(pending, kind);
  return true;
}

/**
 * Chọn thư mục tool (hộp thoại của hệ điều hành) và xin quyền ghi; nhớ lại cho lần sau. Chọn nhầm chính thư mục
 * «Đã Upload» (07/10/2026, chủ dự án gặp: «Không thấy thư mục … trong «Đã Upload»») thì từ chối ngay, vì từ trong
 * đó không với ra các thư mục video nằm cạnh được.
 */
export async function pickToolRoot(kind: UploadKind = 'long'): Promise<boolean> {
  if (!supportsToolFolder()) return false;
  const picker = (window as unknown as { showDirectoryPicker: Picker }).showDirectoryPicker;
  try {
    const handle = await picker({ mode: 'readwrite', id: pickerIdOf(kind) });
    if (handle.name === UPLOADED_FOLDER) {
      throw new Error(
        `Bạn vừa chọn chính thư mục «${UPLOADED_FOLDER}» — hãy chọn thư mục CHỨA các thư mục video (thư mục cha của nó)`,
      );
    }
    await adoptRoot(handle, kind);
    await storeRoot(handle, kind);
    return true;
  } catch (error) {
    // Người dùng bấm Huỷ ở hộp thoại → không phải lỗi.
    if (error instanceof DOMException && error.name === 'AbortError') return false;
    throw error;
  }
}

export async function forgetToolRoot(kind: UploadKind = 'long'): Promise<void> {
  toolFolderStoreOf(kind).setState({ root: null, pending: null, name: '' });
  await storeRoot(null, kind);
}

/**
 * Thư mục của một bộ, lấy từ khoá bộ. Khoá bộ là «<thư mục tương đối>/<tên file video>» («KÊNH 0007/KÊNH 0007.mp4»,
 * «Output/KÊNH 0007/KÊNH 0007.mp4») → trả phần thư mục («KÊNH 0007», «Output/KÊNH 0007»); file rời (khoá không có
 * «/») → ''. Bản đầu (07/10/2026 sáng) cắt nhầm đoạn CUỐI, tức tên file, nên luôn báo «Không thấy thư mục «….mp4»».
 */
export function folderNameOf(releaseKey: string): string {
  const cut = releaseKey.lastIndexOf('/');
  return cut < 0 ? '' : releaseKey.slice(0, cut).trim();
}

/** Tên chưa có trong danh sách: «Tên», «Tên (2)», «Tên (3)»… */
export function uniqueName(existing: readonly string[], name: string): string {
  const taken = new Set(existing.map((item) => item.toLowerCase()));
  if (!taken.has(name.toLowerCase())) return name;
  for (let n = 2; ; n += 1) {
    const candidate = `${name} (${n})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

async function listNames(directory: ToolDirectoryHandle): Promise<string[]> {
  const names: string[] = [];
  for await (const entry of directory.values()) names.push(entry.name);
  return names;
}

/** Chép từng khúc cỡ này khi trình duyệt không dời được file — đủ lớn để nhanh, đủ nhỏ để tiến độ nhúc nhích. */
export const COPY_CHUNK_BYTES = 32 * 1024 * 1024;

export interface MoveOptions {
  /** Báo tiến độ khi phải CHÉP: byte đã đưa sang / tổng byte của thư mục. Dời tức thì thì không gọi. */
  onProgress?: (done: number, total: number) => void;
  /** Cỡ khúc chép — chỉ kiểm thử mới đổi. */
  chunkBytes?: number;
}

export interface MoveResult {
  /** Tên thư mục ở chỗ mới (trong «Đã Upload»). */
  name: string;
  /** Có file phải chép vì trình duyệt không dời được — chậm hơn dời tức thì. */
  copied: boolean;
}

/** Danh sách mục LẤY TRƯỚC khi sửa thư mục — vừa duyệt vừa dời mục ra có thể bỏ sót. */
async function listEntries(
  directory: ToolDirectoryHandle,
): Promise<(ToolFileHandle | ToolDirectoryHandle)[]> {
  const entries: (ToolFileHandle | ToolDirectoryHandle)[] = [];
  for await (const entry of directory.values()) entries.push(entry);
  return entries;
}

/** Tổng cỡ file trong thư mục (kể cả thư mục con) — mẫu số của tiến độ. */
async function directorySize(directory: ToolDirectoryHandle): Promise<number> {
  let total = 0;
  for (const entry of await listEntries(directory)) {
    total += entry.kind === 'directory' ? await directorySize(entry) : (await entry.getFile()).size;
  }
  return total;
}

interface Transfer {
  done: number;
  total: number;
  copied: boolean;
  /** Trình duyệt đã báo không dời được (NotSupportedError) — khỏi thử lại với từng file. */
  moveUnsupported: boolean;
  options: MoveOptions;
}

/** Dời một file bằng `move()` của trình duyệt — tức thì trên cùng ổ. `false` = không dời được, sẽ chép. */
async function tryMove(
  entry: ToolFileHandle,
  target: ToolDirectoryHandle,
  transfer: Transfer,
): Promise<boolean> {
  if (transfer.moveUnsupported || typeof entry.move !== 'function') return false;
  try {
    await entry.move(target);
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotSupportedError') {
      transfer.moveUnsupported = true;
    }
    return false;
  }
}

/** Chép file từng khúc, báo tiến độ sau mỗi khúc. */
async function copyFile(
  name: string,
  file: File,
  target: ToolDirectoryHandle,
  transfer: Transfer,
): Promise<void> {
  const chunk = transfer.options.chunkBytes ?? COPY_CHUNK_BYTES;
  const writable = await (await target.getFileHandle(name, { create: true })).createWritable();
  try {
    for (let offset = 0; offset < file.size;) {
      const end = Math.min(offset + chunk, file.size);
      await writable.write(file.slice(offset, end));
      transfer.done += end - offset;
      transfer.options.onProgress?.(transfer.done, transfer.total);
      offset = end;
    }
  } finally {
    await writable.close();
  }
}

/** Đưa mọi file của `source` sang `target` (thư mục con tạo mới rồi đệ quy): dời được thì dời, không thì chép. */
async function transferDirectory(
  source: ToolDirectoryHandle,
  target: ToolDirectoryHandle,
  transfer: Transfer,
): Promise<void> {
  for (const entry of await listEntries(source)) {
    if (entry.kind === 'directory') {
      const sub = await target.getDirectoryHandle(entry.name, { create: true });
      await transferDirectory(entry, sub, transfer);
      continue;
    }
    const file = await entry.getFile();
    if (await tryMove(entry, target, transfer)) {
      transfer.done += file.size;
      continue;
    }
    transfer.copied = true;
    await copyFile(entry.name, file, target, transfer);
  }
}

/**
 * Chuyển thư mục của bộ (`folderPath`: đường dẫn tương đối so với chỗ kéo vào — «KÊNH 0007», «Output/KÊNH 0007»)
 * vào «Đã Upload» NẰM CẠNH nó. Thư mục tool người dùng chọn có thể là thư mục cha cao hơn hay thấp hơn chỗ đã kéo
 * vào, nên tìm bằng cách thử trọn đường dẫn rồi bỏ dần các đoạn đầu; thấy ở đâu thì «Đã Upload» đặt trong thư mục
 * cha ở đó. Đưa file sang thư mục tên chưa trùng (dời tức thì, không được mới chép) rồi xoá thư mục cũ; trả về tên
 * thư mục ở chỗ mới và có phải chép không. Không thấy → lỗi có lời.
 */
export async function moveToUploaded(
  root: ToolDirectoryHandle,
  folderPath: string,
  options: MoveOptions = {},
): Promise<MoveResult> {
  const segments = folderPath
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  const name = segments.at(-1);
  if (!name || segments.includes(UPLOADED_FOLDER)) throw new Error('Bộ này không có thư mục tool');
  let parent: ToolDirectoryHandle | null = null;
  let source: ToolDirectoryHandle | null = null;
  for (let start = 0; start < segments.length && source === null; start += 1) {
    try {
      let directory = root;
      for (const segment of segments.slice(start, -1)) {
        directory = await directory.getDirectoryHandle(segment);
      }
      source = await directory.getDirectoryHandle(name);
      parent = directory;
    } catch {
      // Không có ở mức này — thử đường dẫn ngắn hơn.
    }
  }
  if (source === null || parent === null) {
    throw new Error(
      `Không thấy thư mục «${name}» trong «${root.name}» — chọn thư mục chứa các thư mục video (không phải «${UPLOADED_FOLDER}»)`,
    );
  }
  const uploaded = await parent.getDirectoryHandle(UPLOADED_FOLDER, { create: true });
  const targetName = uniqueName(await listNames(uploaded), name);
  const target = await uploaded.getDirectoryHandle(targetName, { create: true });
  const transfer: Transfer = {
    done: 0,
    total: await directorySize(source),
    copied: false,
    moveUnsupported: false,
    options,
  };
  await transferDirectory(source, target, transfer);
  await parent.removeEntry(name, { recursive: true });
  return { name: targetName, copied: transfer.copied };
}
